import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  GraduationCap,
  LineChart,
  MessageCircle,
  Sparkles,
  ChevronDown,
  CirclePause,
  CirclePlay
} from "lucide-react";

const PhoneCarousel = () => {
  const slides = [
    { src: "images/1.png", alt: "LearnRussianCases app screen preview 1" },
    { src: "images/2.png", alt: "LearnRussianCases app screen preview 2" },
    { src: "images/3.png", alt: "LearnRussianCases app screen preview 3" },
    { src: "images/4.png", alt: "LearnRussianCases app screen preview 4" },
    { src: "images/5.png", alt: "LearnRussianCases app screen preview 5" },
    { src: "images/6.png", alt: "LearnRussianCases app screen preview 6" },
  ];

  const SLIDE_DURATION = 4500; // ms
  const RADIUS = 7;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [progress, setProgress] = React.useState(0); // 0 → 1
  const [isPaused, setIsPaused] = React.useState(false);
  const elapsedRef = React.useRef(0);

  // Auto-advance + circular progress
  React.useEffect(() => {
    if (isPaused) return;

    const start = Date.now() - elapsedRef.current;

    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      elapsedRef.current = elapsed;

      const fraction = Math.min(elapsed / SLIDE_DURATION, 1);
      setProgress(fraction);

      if (fraction >= 1) {
        clearInterval(interval);
        elapsedRef.current = 0;
        setProgress(0);
        setCurrentIndex(prev => (prev + 1) % slides.length);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [currentIndex, isPaused, slides.length]);

  const handleDotClick = (index) => {
    elapsedRef.current = 0;
    setProgress(0);
    setCurrentIndex(index);
  };

  const handleTogglePause = () => {
    setIsPaused(prev => !prev);
  };

  const currentSlide = slides[currentIndex];

  return (
    <div className="flex justify-center md:justify-end items-center relative z-30 w-full md:w-1/2">
      <div className="relative flex flex-col items-center">
        <picture>
          <source srcSet={currentSlide.src} type="image/png" />
          <img
            src={currentSlide.src}
            alt={currentSlide.alt}
            className="h-[55vh] sm:h-[60vh] md:h-[72vh] max-h-[720px] w-auto z-10 rounded-2xl md:rounded-3xl border shadow-lg border-[#383838]/20"
          />
        </picture>

        <div className="mt-3 w-full flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={handleTogglePause}
            aria-pressed={isPaused}
          >
            {isPaused ? (
              <CirclePlay
                size={20}
                color='rgba(56,56,56,0.9)'
                aria-hidden="true"
                strokeWidth={1.4}
                className="shrink-0 transition-transform duration-200 group-open:rotate-180 bg-white rounded-full"
              />
            ) : (
              <CirclePause
                size={20}
                color='rgba(56,56,56,0.9)'
                aria-hidden="true"
                strokeWidth={1.4}
                className="shrink-0 transition-transform duration-200 group-open:rotate-180 bg-white rounded-full"
              />
            )}
          </button>

          <div className="flex justify-end flex-1 gap-2">
            {slides.map((_, index) => {
              const isActive = index === currentIndex;

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDotClick(index)}
                  className="relative h-5 w-5 flex items-center justify-center"
                  aria-label={`Go to slide ${index + 1}`}
                >
                  <span className="absolute h-4 w-4 rounded-full bg-white/90 border border-[#383838]/50" />
                  <svg
                    viewBox="0 0 20 20"
                    className="absolute h-6 w-6 -rotate-90"
                  >
                    <circle
                      cx="10"
                      cy="10"
                      r={RADIUS}
                      fill="transparent"
                      stroke={!isActive ? "rgba(56,56,56,0)" : "rgba(56,56,56,0.9)"}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeDasharray={CIRCUMFERENCE}
                      strokeDashoffset={CIRCUMFERENCE * (1 - progress)}
                    />
                  </svg>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const items = [
    { icon: BookOpen, label: "Learn", desc: "38 bite-sized lessons", color: "#FFBA08" },
    { icon: GraduationCap, label: "Practice", desc: "Varied, high quality content", color: "#00B5D9" },
    { icon: LineChart, label: "Progress", desc: "Track each case", color: "#8FBF6E" },
    { icon: MessageCircle, label: "Vocab", desc: "Flashcards for all cases", color: "#E83366" },
    {
      icon: Sparkles,
      label: "Premium",
      desc: (
        <>
          $2.99, once. Lifetime updates.
          <br />
          No ads. No subscription.
        </>
      ),
      color: "#ADA6C9"
    },
  ];

  const toRgba = (hex, a = 0.2) => {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const n = parseInt(full, 16);
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  const formattedPrice = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
  }).format(2.99);

  const CASE_COLORS = {
    Nominative: '#6C757D',
    Accusative: '#FFBA08',
    Prepositional: '#00B4D8',
    Genitive: '#E73265',
    Dative: '#90BE6D',
    Instrumental: '#ADA7C9',
  };

  const CaseCard = ({ title, color, children }) => (
    <div
      className="rounded-2xl border p-4"
      style={{
        borderColor: color,
        backgroundColor: toRgba(color, 0.12)
      }}
    >
      <h3
        className="font-semibold mb-2"
        style={{ color }}
      >
        {title}
      </h3>
      <ul className="list-disc list-inside space-y-1">
        {children}
      </ul>
    </div>
  );

  const IconLabel = ({ Icon, color, children }) => (
    <h3 className="font-semibold mb-2 flex items-center gap-3 md:gap-4 text-lg md:text-xl">
      <span
        className="inline-flex items-center justify-center rounded-full"
        style={{
          width: 36, height: 36,
          border: `1px solid ${color}`,
          backgroundColor: toRgba(color, 0.2)
        }}
        aria-hidden
      >
        <Icon size={22} color={color} />
      </span>
      <span>{children}</span>
    </h3>
  );

  const FAQItem = ({ title, children }) => {
    const ref = React.useRef(null);

    const toggle = () => { if (ref.current) ref.current.open = !ref.current.open; };

    const onClick = (e) => {
      const tag = e.target.tagName;
      if (['A', 'BUTTON', 'INPUT', 'TEXTAREA', 'SELECT', 'SUMMARY'].includes(tag)) return;
      toggle();
    };

    const onKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
    };

    return (
      <details
        ref={ref}
        onClick={onClick}
        onKeyDown={onKeyDown}
        tabIndex={0}
        className="group cursor-pointer relative bg-white/50 shadow-lg w-full max-w-[1000px] z-30 mx-auto p-4 md:p-6 border border-[#383838]/20 rounded-3xl focus:outline-none focus:ring-2 focus:ring-[#1f1f1f]/20"
      >
        <summary className="list-none font-semibold flex items-center gap-x-3 md:gap-x-4">
          <ChevronDown
            size={20}
            aria-hidden="true"
            className="shrink-0 transition-transform duration-200 group-open:rotate-180"
          />
          <span>{title}</span>
        </summary>
        <div className="mt-2">{children}</div>
      </details>
    );
  };

  return (
    <div className="bg-[#f1f0ee] font-semibold text-[#1f1f1f] font-serif min-h-screen flex flex-col justify-between overflow-x-hidden">

      {/* Grid background */}
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: `
              linear-gradient(to right, #ddd 1px, transparent 1px),
              linear-gradient(to bottom, #ddd 1px, transparent 1px)
            `,
          backgroundSize: "32px 32px",
          opacity: 1,
          maskImage: "radial-gradient(circle at center, white, transparent 75%)",
          WebkitMaskImage: "radial-gradient(circle at center, white, transparent 75%)"
        }}
        aria-hidden="true"
      />

      {/* Top bar */}
      <div className="bg-white/50 shadow-lg h-14 md:h-16 md:w-full max-w-[1000px] z-30 mt-3 md:m-4 md:mx-auto px-3 md:px-4 border border-[#383838]/20 rounded-2xl md:rounded-3xl mx-3">
        <div className="flex h-full items-center justify-between">
          <Link to="/" className="min-w-0">
            <div className="flex items-center">
              <img src="images/lrc_icon.png" alt="LearnRussianCases icon" className="w-7 h-7 md:w-8 md:h-8 rounded-full mr-2" />
              <p className="text-[#1f1f1f] font-nunito text-lg md:text-2xl truncate">LearnRussianCases</p>
            </div>
          </Link>

          <div className="flex flex-row gap-x-4 md:gap-x-8">
            <Link to="/support"><p className="text-[#1f1f1f] font-nunito text-sm md:text-lg">Support</p></Link>
            <Link to="/privacy"><p className="text-[#1f1f1f] font-nunito text-sm md:text-lg">Privacy</p></Link>
          </div>
        </div>
      </div>

      {/* Headline – single H1 */}
      <div className="mt-6 md:mt-8 mx-auto z-30 w-full md:w-auto max-w-[1000px]">
        <h1 className="text-center md:text-left leading-tight">
          <span className="font-joan block mb-1 md:mb-0 text-2xl md:text-4xl">
            Master the{' '}
          </span>
          <span className="font-bebas tracking-wide block text-7xl md:text-8xl leading-none">
            Russian
            <br className="md:hidden" />
            <span className="hidden md:inline">&nbsp;</span>
            case system
          </span>
        </h1>
      </div>

      {/* Store buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center mt-4 md:mt-8 gap-4 sm:gap-x-12 z-30 px-4">
        <a href="https://apps.apple.com/us/app/learnrussiancases/id6450753430" className="inline-block" target="_blank" rel="noopener noreferrer">
          <img src="images/appStore.svg" alt="Download on the App Store" className="h-12 md:h-14 object-contain" />
        </a>

        <div className="relative inline-block">
          <div
            className="absolute left-1/2 -translate-x-1/2 -top-2 md:-top-4
               bg-[#FFBA08] py-1.5 px-3 rounded z-10 shadow-xl
               pointer-events-none select-none">
            <p className="font-nunito text-[11px] md:text-xs leading-none whitespace-nowrap">
              COMING SOON
            </p>
          </div>

          <img src="images/googlePlay.png" alt="Get it on Google Play"
            className="h-12 md:h-14 object-contain opacity-50" />
        </div>
      </div>

      {/* Content row */}
      <div className="relative flex flex-col md:flex-row gap-8 md:gap-x-12 items-start h-full w-full mt-8 md:mt-12 px-4">
        {/* Phone carousel */}
        <PhoneCarousel />

        {/* Feature list */}
        <div className="flex flex-col justify-center mx-auto items-start z-30 gap-y-6 md:gap-y-8 md:w-1/2">
          {items.map(({ icon: Icon, label, desc, color }) => (
            <div key={label} className="flex items-center gap-x-3 md:gap-x-4">
              <div className="relative flex bg-white h-12 w-12 md:h-16 md:w-16 rounded-full items-center justify-center">
                <div
                  style={{ border: `1px solid ${color}`, backgroundColor: toRgba(color, 0.2) }}
                  className='absolute inset-0 rounded-full flex items-center justify-center'
                  aria-hidden
                />
                <Icon color={color} size={26} className="md:hidden" aria-hidden />
                <Icon color={color} size={32} className="hidden md:block" aria-hidden />
              </div>
              <div className="flex-col">
                <div className="font-bebas tracking-wide text-xl md:text-2xl">{label}</div>
                <div className="font-nunito text-[15px] md:text-base">{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ---------- SEO Content ---------- */}
      <main id="seo-content" className="z-30 w-full mt-14 md:mt-20">
        {/* What are cases */}
        <section id="what-are-cases" aria-labelledby="what-are-cases-title" className="m-4 md:m-0">
          <h2 id="what-are-cases-title" className="font-bebas text-4xl md:text-5xl w-full md:w-[1000px] mx-auto tracking-wide mb-3 md:mb-4 md:pl-8 text-center md:text-left">
            What are Russian cases?
          </h2>
          <div className='relative bg-white/50 shadow-lg w-full md:w-[1000px] z-30 m-4 mx-auto p-6 border border-[#383838]/20 rounded-3xl'>
            <img src="images/doll_head_smiling.png" alt="Smiling matryoshka doll" className="hidden md:block absolute w-24 h-24 -top-24 right-8" />

            <p className="font-nunito leading-7 mb-4">
              Cases are the most challenging (but essential) part of the Russian language. Russian has 6 cases, that affect nouns, adjectives, pronouns, and numerals.
            </p>

            <p className="font-nunito leading-7 mb-4">
              Word endings <em>decline</em> (change) to show how words relate to each other in a sentence. Let's consider the word <code className='bg-[#403d39] p-[2px] px-[6px] rounded text-white'>книга</code> (book). In English, the spelling stays the
              same, but in Russian it changes depending on how it's used:
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#fff] border border-[#ccc] text-2xl">📖</div>
                <div className="flex flex-col gap-1.5">
                  <div className="font-nunito">Я читаю <code className='bg-[#FFBA08] p-[2px] px-[6px] rounded text-white'>книгу</code></div>
                  <div className="font-nunito text-sm opacity-80">I am reading the <code className='bg-[#ccc] p-[2px] px-[6px] rounded text-[#1f1f1f]'>book</code></div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#fff] border border-[#ccc] text-2xl">📖</div>
                <div className="flex flex-col gap-1.5">
                  <div className="font-nunito">Я прочитал это в <code className='bg-[#00B4D8] p-[2px] px-[6px] rounded text-white'>книге</code></div>
                  <div className="font-nunito text-sm opacity-80">I read it in a <code className='bg-[#ccc] p-[2px] px-[6px] rounded text-[#1f1f1f]'>book</code></div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#fff] border border-[#ccc] text-2xl">📖</div>
                <div className="flex flex-col gap-1.5">
                  <div className="font-nunito">У меня нет <code className='bg-[#E73265] p-[2px] px-[6px] rounded text-white'>книги</code></div>
                  <div className="font-nunito text-sm opacity-80">I don't have a <code className='bg-[#ccc] p-[2px] px-[6px] rounded text-[#1f1f1f]'>book</code></div>
                </div>
              </div>
            </div>

            <p className="font-nunito mt-4">
              The declination depends on the case, the gender, and whether something is singular or plural. LearnRussianCases breaks this down into tiny steps with clear examples and explanations on why and how to use each case and its declensions.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" aria-labelledby="how-it-works-title" className="m-4 md:m-0 mt-12 md:mt-20">
          <h2 id="how-it-works-title" className="font-bebas text-4xl md:text-5xl w-full md:w-[1000px] mx-auto tracking-wide mb-3 md:mb-4 md:pl-8 text-center md:text-left">
            How the App Works
          </h2>
          <div className='relative bg-white/50 shadow-lg w-full md:w-[1000px] z-30 m-4 mx-auto p-6 border border-[#383838]/20 rounded-3xl'>
            <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-6 md:mb-8">
              <div className="font-nunito">
                <IconLabel Icon={BookOpen} color="#FFBA08">38 Simple Lessons</IconLabel>
                <p>Each lesson targets a single pattern&dagger; (e.g., accusative masculine nouns) with plain, example-led explanations.</p>
              </div>

              <div className="font-nunito">
                <IconLabel Icon={GraduationCap} color="#00B5D9">Focused Practice</IconLabel>
                <p>Strengthen your understanding with a variety of engaging question types and get instant feedback as you learn.</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              <div className="font-nunito">
                <IconLabel Icon={LineChart} color="#8FBF6E">Progress tracking</IconLabel>
                <p>Track which cases you've mastered and which to review next. Stay motivated and unlock all the achievements!</p>
              </div>

              <div className="font-nunito">
                <IconLabel Icon={MessageCircle} color="#E83366">Flashcards</IconLabel>
                <p>Browse and practice flashcards drawn from all question content, covering every case of a noun or adjective.</p>
              </div>
            </div>

            <p className='font-nunito text-xs md:text-sm mt-4'>&dagger; except nominative lessons, which are more introductory.</p>
          </div>
        </section>

        {/* Curriculum outline */}
        <section id="curriculum" aria-labelledby="curriculum-title" className="m-4 md:m-0 mt-12 md:mt-20">
          <h2 id="curriculum-title" className="font-bebas text-4xl md:text-5xl w-full md:w-[1000px] mx-auto tracking-wide mb-3 md:mb-4 md:pl-8 text-center md:text-left">
            The Curriculum
          </h2>
          <div className='relative bg-white/50 shadow-lg w-full md:w-[1000px] z-30 m-4 mx-auto p-6 border border-[#383838]/20 rounded-3xl'>
            <p className='font-nunito mb-4'>The Russian case system is divided into 38 bite-sized lessons, each focused on a specific case–gender–type (e.g., accusative masculine nouns). Three nominative lessons introduce the core fundamentals.</p>
            <div className="grid md:grid-cols-3 gap-4 md:gap-6 font-nunito">
              <CaseCard title="Nominative" color={CASE_COLORS.Nominative}>
                <>
                  <li>Identifying grammatical gender</li>
                  <li>Marking the subject</li>
                  <li>Forming plurals</li>
                </>
              </CaseCard>

              <CaseCard title="Accusative" color={CASE_COLORS.Accusative}>
                <>
                  <li>Marking the direct object</li>
                  <li>Motion & accusative prepositions</li>
                  <li>Animacy</li>
                </>
              </CaseCard>

              <CaseCard title="Genitive" color={CASE_COLORS.Genitive}>
                <>
                  <li>Possession</li>
                  <li>Negation & absence</li>
                  <li>Numbers & quantities</li>
                </>
              </CaseCard>

              <CaseCard title="Dative" color={CASE_COLORS.Dative}>
                <>
                  <li>Marking the indirect object</li>
                  <li>Motion & dative prepositions</li>
                  <li>Giving, telling, showing</li>
                </>
              </CaseCard>

              <CaseCard title="Instrumental" color={CASE_COLORS.Instrumental}>
                <>
                  <li>With whom or with what</li>
                  <li>Parts of the day & seasons</li>
                  <li>Instrumental prepositions</li>
                </>
              </CaseCard>

              <CaseCard title="Prepositional" color={CASE_COLORS.Prepositional}>
                <>
                  <li>Location</li>
                  <li>Usage after certain prepositions</li>
                </>
              </CaseCard>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" aria-labelledby="faq-title" className="m-4 md:m-0 mt-12 md:mt-20 mb-16 md:mb-20">
          <h2 id="faq-title" className="font-bebas text-4xl md:text-5xl w-full md:w-[1000px] mx-auto tracking-wide mb-3 md:mb-4 md:pl-8 text-center md:text-left">FAQ</h2>
          <div className="space-y-3 md:space-y-4 font-nunito">
            <FAQItem title="Is this for beginners or intermediates?">
              <p className="mt-2">
                Both. Cases are a fundamental part of learning Russian, so it’s great to start early. Beginners will see more unfamiliar words than intermediates, but that’s perfectly fine — English translations guide you throughout, and once you learn the declension rules, you can apply them to any new vocabulary.
              </p>
            </FAQItem>

            <FAQItem title="How long are the lessons?">
              <p className="mt-2">
                Bite-sized — most take 5–10 minutes. Keep coming back to practice; you’ll get varied question types and instant feedback, with fresh items each session.
              </p>
            </FAQItem>

            <FAQItem title="Do I need prior Russian?">
              <p className="mt-2">
                A little prior Russian is assumed — you should be able to read Cyrillic and form simple sentences. You’ll encounter some new vocabulary (a feature, not a bug!), but since we’re teaching cases, you’ll be able to apply the rules to any word you learn.
              </p>
            </FAQItem>

            <FAQItem title="How much does it cost?">
              <p className="mt-2">
                A one-time purchase of {formattedPrice} (or your local equivalent). No subscriptions, no ads. Lifetime updates included.
              </p>
            </FAQItem>

            <FAQItem title="Where can I get help?">
              <p className="mt-2">
                Visit <Link to="/support" className="underline decoration-[#1f1f1f]/50">Support</Link> or see our <Link to="/privacy" className="underline decoration-[#1f1f1f]/50">Privacy</Link> page.
              </p>
            </FAQItem>
          </div>
        </section>

        {/* Store buttons bottom */}
        <section aria-labelledby="stores-bottom" className="w-full max-w-[1000px] mx-auto px-4 mt-12 md:mt-20 mb-16 md:mb-20">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-x-12 z-30">
            <a href="https://apps.apple.com/us/app/learnrussiancases/id6450753430" className="inline-block" target="_blank" rel="noopener noreferrer">
              <img src="images/appStore.svg" alt="Download on the App Store" className="h-12 md:h-14 object-contain" />
            </a>

            <div className='relative inline-block'>
              <div
                className="absolute left-1/2 -translate-x-1/2 -top-2 md:-top-4
               bg-[#FFBA08] py-1.5 px-3 rounded z-10 shadow-xl
               pointer-events-none select-none">
                <p className="font-nunito text-[11px] md:text-xs leading-none whitespace-nowrap">
                  COMING SOON
                </p>
              </div>

              <img src="images/googlePlay.png" alt="Get it on Google Play"
                className="h-12 md:h-14 object-contain opacity-50" />
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white/50 shadow-lg h-14 md:h-16 md:w-full max-w-[1000px] z-30 mx-3 md:m-4 mb-4 md:mb-8 md:mx-auto px-4 border border-[#383838]/20 rounded-2xl md:rounded-3xl">
        <div className="flex h-full items-center justify-center">
          <a href="https://josephmalam.com/" target="_blank" className="font-nunito px-2 text-sm md:text-base" rel="noopener noreferrer">
            &copy; <span className='underline decoration-[#1f1f1f]/50'>Joseph</span> and Anna Malam 2022-2025
          </a>
        </div>
      </footer>
    </div >
  );
};

export default Home;
